import { debounce, isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { getPDPUrl } from 'components/product-comparison-grid/product-comparison-card/utils';
import { DEBOUNCE_TIME_MILLISECONDS } from 'constants/common';
import { HEADLESS_SEARCH_PAGE } from 'constants/links';
import { useParams, useRouter } from 'next/navigation';
import { useState } from 'react';
import { useEsiteCtx } from 'services/esite/context';
import { useSearchSuggestionQuery } from 'services/searchSuggestions/use-search-suggestions-query';
import { getSeoUrl } from 'utils/links';
import { getSuggestedDiagrams, getSuggestedKeywords } from './utils';

const useSearchSuggestions = ({
  isHeadlessPDPEnabled,
  isFederatedSearchEnabled,
  model,
  serialNumber
}: {
  isHeadlessPDPEnabled?: boolean;
  isFederatedSearchEnabled?: boolean;
  model?: string;
  serialNumber?: string;
} = {}) => {
  const { catalogId, langId, storeId, locale, esiteIdentifier } = useEsiteCtx();
  const [searchValue, setSearchValue] = useState('');
  const params = useParams();
  const { data, isPending, isFetching } = useSearchSuggestionQuery({
    searchTerm: searchValue,
    storeIdentifier: esiteIdentifier,
    storeId,
    catalogId,
    langId,
    locale,
    serialNumber,
    esite: params.esite
  });
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const esiteUrl = getSeoUrl(params);
  const suggestions = {
    suggestedKeyWords:
      getSuggestedKeywords(
        data?.suggestedKeyWords,
        model,
        serialNumber,
        esiteUrl
      ) ?? [],
    suggestedProducts: data?.suggestedProducts ?? [],
    suggestedCategories: data?.suggestedCategories ?? [],
    suggestedDiagrams:
      getSuggestedDiagrams(data?.suggestedDiagrams, esiteUrl) ?? []
  };

  const shouldNavigateToPdp = suggestions.suggestedProducts.length === 1;
  const shouldNavigateToPartsDiagram =
    isFederatedSearchEnabled &&
    suggestions.suggestedDiagrams.length === 1 &&
    isEmpty(suggestions.suggestedProducts);

  const getSuggestions = debounce((searchTerm: string) => {
    setSearchValue(searchTerm);
  }, DEBOUNCE_TIME_MILLISECONDS);

  const { push } = useRouter();

  const navigateToPdp = () => {
    const [{ value = '', partNumber }] = suggestions.suggestedProducts;

    const pdpURL = getPDPUrl({
      esiteParams: params,
      isHeadlessPDPEnabled,
      productNumber: partNumber,
      productURL: value
    });

    if (isHeadlessPDPEnabled) {
      push(pdpURL);
    } else {
      window.location.href = pdpURL;
    }
  };

  const navigateToHeadlessSearch = (searchTerm: string) => {
    const pageParam = isFederatedSearchEnabled
      ? { p_page: '1' }
      : { page: '1' };
    const stringifiedParams = new URLSearchParams({
      ...pageParam,
      ...(model && { model, filterByEquipment: 'true' }),
      ...(serialNumber && { serialNumber })
    });
    const basePath = replaceTokensInString(HEADLESS_SEARCH_PAGE, searchTerm);

    window.location.href = `${esiteUrl}${basePath}&${stringifiedParams.toString()}`;
  };

  const naviagetToPartsDiagram = () => {
    const [{ value }] = suggestions.suggestedDiagrams;
    window.location.href = value;
  };

  const onSearch = (searchTerm: string) => {
    if (!searchTerm) return;

    if (shouldNavigateToPdp) {
      navigateToPdp();
    } else if (shouldNavigateToPartsDiagram) {
      naviagetToPartsDiagram();
    } else {
      navigateToHeadlessSearch(searchTerm);
    }
  };

  const hideResults = isFetching || isPending;

  return { onSearch, getSuggestions, suggestions, hideResults, searchValue };
};

export default useSearchSuggestions;
